import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Loader2, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { useAuthStore } from '@/shared/stores/auth-store';
import { getSocket } from '@/features/atendimento/socket';
import { refreshQrChannel, restartQrChannel } from './api';

/**
 * Componente reusável de pareamento via QR Code (canal qr_code/whatsmeow).
 * Subscribe Socket.IO room `tenant:${tenantId}:qr-setup:${numberId}`,
 * renderiza QR string crua via qrcode.react.
 *
 * Usado dentro do QrConnectModal (wizard) e da página /canais/conectar-qr/:id.
 *
 * Tech-spec: (removido — vide whatsmeow worker) (Task H2).
 */

type State =
  | { kind: 'connecting' }
  | { kind: 'qr'; qrString: string; attempt: number }
  | { kind: 'open' }
  | { kind: 'close'; reason: string };

export interface QrPairingViewProps {
  numberId: number;
  /** Chamado quando o pareamento concluir (status open) ou user clicar em fechar/voltar. */
  onClose: () => void;
  /** Texto do botão de voltar/fechar (default: "Voltar para Canais"). */
  closeLabel?: string;
  /**
   * "pair" (default): chama /qr-code/refresh — fluxo de pareamento. QR aparece
   * pra escanear, status vira active após scan.
   * "restart": chama /qr-code/restart — reconecta com auth state preservado.
   * Se auth state existe, vai direto pra "Conectado!" sem mostrar QR.
   */
  mode?: 'pair' | 'restart';
}

export function QrPairingView({
  numberId,
  onClose,
  closeLabel = 'Voltar para Canais',
  mode = 'pair',
}: QrPairingViewProps): JSX.Element {
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  const [state, setState] = useState<State>({ kind: 'connecting' });
  const [refreshing, setRefreshing] = useState(false);
  // Vira true quando QR aparece pela 1ª vez. Permite distinguir o "connecting"
  // ANTES do QR (esperando primeiro QR) do "connecting" APÓS scan (handshake
  // em curso) — só esconde o botão "Gerar novo QR" no segundo caso.
  const [qrAlreadyShown, setQrAlreadyShown] = useState(false);
  const noQrTimeout = useRef<NodeJS.Timeout | null>(null);
  // Ref pra que o listener `onClosed` leia valor atualizado sem re-criar
  // o effect (evita flash de "Conexão perdida" durante regenerate intermediário).
  const refreshingRef = useRef(false);

  const handleRefresh = async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    setState({ kind: 'connecting' });
    try {
      await refreshQrChannel(numberId);
    } catch (err) {
      console.error('refresh qr failed', err);
    } finally {
      setTimeout(() => {
        refreshingRef.current = false;
        setRefreshing(false);
      }, 4000);
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    const socket = getSocket();

    socket.emit('joinQrSetup', numberId);

    // mode='pair': dispara regenerate imediato (worker fecha+reabre socket
    // pra QR fresh em ~3s).
    // mode='restart': dispara restart (worker abre socket com auth state
    // preservado — vai direto pra "Conectado!" se creds válidas).
    refreshingRef.current = true;
    setRefreshing(true);
    const initialAction = mode === 'restart' ? restartQrChannel : refreshQrChannel;
    void initialAction(numberId).catch((err) => {
      console.error(`initial ${mode} failed`, err);
    });
    // Janela de 5s pra ignorar qr.close intermediário
    if (noQrTimeout.current) clearTimeout(noQrTimeout.current);
    noQrTimeout.current = setTimeout(() => {
      refreshingRef.current = false;
      setRefreshing(false);
    }, 5000);

    const onConnecting = () => setState({ kind: 'connecting' });
    const onQrRefresh = (payload: { qrString: string; attempt: number }) => {
      if (noQrTimeout.current) {
        clearTimeout(noQrTimeout.current);
        noQrTimeout.current = null;
      }
      // QR chegou — refresh terminou, reabilita captura de close real
      refreshingRef.current = false;
      setRefreshing(false);
      setQrAlreadyShown(true);
      setState({ kind: 'qr', qrString: payload.qrString, attempt: payload.attempt });
    };
    const onOpen = () => {
      setState({ kind: 'open' });
      // Auto-fecha após 2s — pareamento concluído
      setTimeout(() => onClose(), 2000);
    };
    const onClosed = (payload: { reason: string }) => {
      // Suprime close intermediário durante auto-refresh/manual-refresh
      // (worker fecha socket pra regenerar — esse close é esperado, não erro).
      // logged_out é tratado normalmente — é estado terminal real.
      if (refreshingRef.current && payload.reason !== 'logged_out') return;
      setState({ kind: 'close', reason: payload.reason });
    };

    socket.on('qr.connecting', onConnecting);
    socket.on('qr.refresh', onQrRefresh);
    socket.on('qr.open', onOpen);
    socket.on('qr.close', onClosed);

    return () => {
      if (noQrTimeout.current) clearTimeout(noQrTimeout.current);
      socket.off('qr.connecting', onConnecting);
      socket.off('qr.refresh', onQrRefresh);
      socket.off('qr.open', onOpen);
      socket.off('qr.close', onClosed);
      socket.emit('leaveQrSetup', numberId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, numberId]);

  return (
    <>
      {state.kind === 'connecting' && (
        <div className="flex flex-col items-center gap-4 py-8">
          <Loader2 size={32} className="animate-spin text-brand-500" />
          <p className="text-[13px] text-n-600">
            {mode === 'restart'
              ? 'Reconectando canal...'
              : refreshing
                ? 'Gerando novo QR...'
                : qrAlreadyShown
                  ? 'Conectando...'
                  : 'Iniciando conexão...'}
          </p>
          {/* Botão "Gerar novo QR" só aparece ANTES do QR ter sido mostrado.
              Após o scan (qrAlreadyShown=true), handshake está em curso e
              regenerar o QR invalidaria — esconder evita confusão UX. */}
          {!refreshing && mode !== 'restart' && !qrAlreadyShown && (
            <button
              type="button"
              onClick={() => void handleRefresh()}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-n-200 px-3 py-1.5 text-[12px] text-n-700 hover:bg-n-50"
            >
              <RefreshCw size={13} />
              Gerar novo QR
            </button>
          )}
        </div>
      )}

      {state.kind === 'qr' && (
        <div className="flex flex-col items-center gap-4">
          <div className="rounded-md border border-n-200 bg-n-0 p-4">
            <QRCodeSVG
              value={state.qrString}
              size={280}
              level="M"
              marginSize={2}
              bgColor="#ffffff"
              fgColor="#000000"
            />
          </div>
          <div className="text-center text-[12px] text-n-600">
            <p className="mb-1 font-medium">Como conectar:</p>
            <ol className="space-y-1 text-left">
              <li>1. Abra o WhatsApp no celular</li>
              <li>2. Toque em ⋮ → "Aparelhos conectados"</li>
              <li>3. Toque em "Conectar um aparelho"</li>
              <li>4. Aponte a câmera para este QR Code</li>
            </ol>
            <p className="mt-3 text-n-400">QR atualiza automaticamente. Tentativa #{state.attempt}.</p>
          </div>
          <button
            type="button"
            onClick={() => void handleRefresh()}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-md border border-n-200 px-3 py-1.5 text-[12px] text-n-700 hover:bg-n-50 disabled:opacity-50"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            Gerar novo QR
          </button>
        </div>
      )}

      {state.kind === 'open' && (
        <div className="flex flex-col items-center gap-4 py-8">
          <CheckCircle2 size={48} className="text-success-500" />
          <p className="text-center text-[14px] font-medium text-n-900">Conectado!</p>
          <p className="text-center text-[12px] text-n-500">Fechando em instantes...</p>
        </div>
      )}

      {state.kind === 'close' && (
        <div className="flex flex-col items-center gap-4 py-8">
          <AlertTriangle size={48} className="text-danger-500" />
          <p className="text-center text-[14px] font-medium text-n-900">
            {state.reason === 'logged_out'
              ? 'Sessão encerrada no celular'
              : 'Conexão perdida'}
          </p>
          <div className="flex gap-2">
            {state.reason !== 'logged_out' && (
              <button
                type="button"
                onClick={() => void handleRefresh()}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-500 px-4 py-2 text-[13px] text-white hover:bg-brand-600 disabled:opacity-50"
              >
                <RefreshCw size={14} />
                Gerar novo QR
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-n-200 px-4 py-2 text-[13px] text-n-700 hover:bg-n-50"
            >
              {closeLabel}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
